import React from 'react';
import { Box, Typography, Button, Modal } from '@mui/material';

const AssetVarianceModal = ({ 
    open, 
    onClose, 
    selectedInvestorForVariance, 
    varianceData 
}) => {
    // Helper function to format numbers with 3 decimal places
    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '-';
        const number = Number(num);

        // Handle very large numbers in scientific notation
        if (Math.abs(number) >= 1e6) {
            // Format in scientific notation with 3 decimal places
            const exponential = number.toExponential(3);
            return `$${exponential}`;
        }

        // For smaller numbers, round to 3 decimal places
        const rounded = Math.round(number * 1000) / 1000;
        return `$${rounded.toFixed(3)}`;
    };

    // Helper function to format percentages with 3 decimal places
    const formatPercentage = (num) => {
        if (!num || isNaN(num)) return '0.000%';
        const number = Number(num);

        // Handle very large numbers in scientific notation
        if (Math.abs(number) >= 1e6) {
            // Format in scientific notation with 3 decimal places
            const exponential = number.toExponential(3);
            return `${exponential}%`;
        }

        // For smaller numbers, round to 3 decimal places
        const rounded = Math.round(number * 1000) / 1000;
        return `${rounded.toFixed(3)}%`;
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
        >
            <Box sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "95%",
                maxWidth: "1200px",
                bgcolor: "#fff",
                borderRadius: "4px",
                padding: "20px 24px",
                outline: "none",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
            }}>
                {/* Header */}
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" style={{ fontSize: '22px', fontWeight: 500 }}>
                            Asset Variance for:
                        </Typography>
                        <Typography variant="h6" className="confirmation-modal-strategy-name">
                            {selectedInvestorForVariance?.userRole?.user?.userInfo?.firstName || selectedInvestorForVariance?.name || 'Investor'}
                        </Typography>
                    </Box>

                    <Button
                        onClick={onClose}
                        sx={{
                            padding: "8px",
                            minWidth: "auto",
                            color: "#666",
                            fontSize: "24px",
                            fontWeight: "600",
                            lineHeight: "32px",
                            fontFamily: "Roboto",
                            fontStyle: "normal",
                            textAlign: "center",
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#333',
                            }
                        }}
                    >
                        ×
                    </Button>
                </Box>

                {/* Table Container */}
                <Box sx={{
                    maxHeight: "600px",
                    overflow: "auto",
                    position: 'relative',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    willChange: 'transform',
                    "&::-webkit-scrollbar": {
                        width: "8px",
                        height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#888",
                        borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#555",
                    },
                }}>
                    {selectedInvestorForVariance && (
                        <table style={{
                            width: "100%",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                            tableLayout: 'fixed',
                        }}>
                            <colgroup>
                                <col style={{ width: '12.5%' }} />
                                <col style={{ width: '12.5%' }} />
                                <col style={{ width: '12.5%' }} />
                                <col style={{ width: '12.5%' }} />
                                <col style={{ width: '12.5%' }} />
                                <col style={{ width: '12.5%' }} />
                                <col style={{ width: '12.5%' }} />
                                <col style={{ width: '12.5%' }} />
                            </colgroup>
                            <thead style={{ position: "sticky", top: 0, backgroundColor: "rgba(210, 215, 233, 0.50)", zIndex: 2 }}>
                                <tr style={{ border: "1px solid #e0e0e0", backgroundColor: "rgba(210, 215, 233, 0.50)" }}>
                                    <th
                                        colSpan={1}
                                        className="asset-variance-table-header"
                                    >
                                        {/* Empty first column */}
                                    </th>
                                    <th
                                        colSpan={2}
                                        className="asset-variance-table-header"
                                    >
                                        ALLOCATION
                                    </th>
                                    <th
                                        colSpan={2}
                                        className="asset-variance-table-header"
                                    >
                                        PORTFOLIO
                                    </th>
                                    <th
                                        colSpan={3}
                                        className="asset-variance-table-header"
                                    >
                                        REQUIRED ACTIVITY FOR ASSETS
                                    </th>
                                </tr>
                                <tr style={{ border: "1px solid #e0e0e0", backgroundColor: "rgba(210, 215, 233, 0.50)" }}>
                                    <th className="asset-variance-table-header">
                                        Asset
                                    </th>
                                    <th className="asset-variance-table-header">
                                        Exis. Allocation
                                    </th>
                                    <th className="asset-variance-table-header">
                                        New Allocation
                                    </th>
                                    <th className="asset-variance-table-header">
                                        Exis. Portfolio
                                    </th>
                                    <th className="asset-variance-table-header">
                                        Ideal Portfolio
                                    </th>
                                    <th className="asset-variance-table-header">
                                        % Variance
                                    </th>
                                    <th className="asset-variance-table-header">
                                        Assets to Buy
                                    </th>
                                    <th className="asset-variance-table-header">
                                        Assets to Sell
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Find the specific investor data from varianceData
                                    const specificInvestorData = varianceData.find(user =>
                                        user?.userRole?.id === selectedInvestorForVariance?.userRole?.id
                                    );

                                    return specificInvestorData?.assetsVariance?.map((asset, index) => {
                                        const currentAllocation = asset.currentPortfolio && asset.idealPortfolio
                                            ? ((Number(asset.currentPortfolio) / (Number(asset.currentPortfolio) + Number(asset.idealPortfolio))) * 100).toFixed(3)
                                            : '0.000';
                                        const newAllocation = asset.idealPortfolio && asset.currentPortfolio
                                            ? ((Number(asset.idealPortfolio) / (Number(asset.currentPortfolio) + Number(asset.idealPortfolio))) * 100).toFixed(3)
                                            : '0.000';
                                        const variance = asset.variance ? Number(asset.variance) : 0;
                                        const assetsToBuy = variance > 0 ? variance : 0;
                                        const assetsToSell = variance < 0 ? Math.abs(variance) : 0;

                                        return (
                                            <tr key={index} style={{ border: "1px solid #e0e0e0" }}>
                                                <td className="asset-variance-table-cell">
                                                    {asset.asset?.symbol || '-'}
                                                </td>
                                                <td className="asset-variance-table-cell-secondary">
                                                    {formatPercentage(currentAllocation)}
                                                </td>
                                                <td className="asset-variance-table-cell-secondary">
                                                    {formatPercentage(newAllocation)}
                                                </td>
                                                <td className="asset-variance-table-cell-secondary">
                                                    {formatNumber(asset.currentPortfolio)}
                                                </td>
                                                <td className="asset-variance-table-cell-secondary">
                                                    {formatNumber(asset.idealPortfolio)}
                                                </td>
                                                <td className="asset-variance-table-cell-secondary">
                                                    {formatPercentage(Math.abs(variance))}
                                                </td>
                                                <td className={assetsToBuy > 0 ? "asset-variance-table-cell-buy" : "asset-variance-table-cell-secondary"}>
                                                    {assetsToBuy > 0 ? formatNumber(assetsToBuy) : '-'}
                                                </td>
                                                <td className={assetsToSell > 0 ? "asset-variance-table-cell-sell" : "asset-variance-table-cell-secondary"}>
                                                    {assetsToSell > 0 ? formatNumber(assetsToSell) : '-'}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default AssetVarianceModal;
